// Página /login dedicada (no modal). Pensada para admins que abren un
// bookmark de /admin sin sesión, y también para alumnos nuevos que aterrizan
// aquí desde un login-gate (ej. /bonos/<slug> con `?next=...`).
//
// Tabs:
//   - login:  email + password
//   - signup: email + password + confirmar + nombre + (opcional) apellidos + tlf
//
// Tras éxito → respeta ?next= si viene; si no, navega por rol (admin/profesor/home).

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../lib/api';

const RX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login, signup, user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({
    email: '',
    password: '',
    password2: '',
    nombre: '',
    apellidos: '',
    telefono: '',
  });
  const [enviando, setEnviando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState(null);
  const [errores, setErrores] = useState({});
  const [retryAfter, setRetryAfter] = useState(null);

  // Redirect si ya está logado
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated && user) {
      const next = params.get('next');
      if (next) navigate(decodeURIComponent(next), { replace: true });
      else if (user.rol === 'admin') navigate('/admin', { replace: true });
      else if (user.rol === 'profesor') navigate('/profesor', { replace: true });
      else navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, user, params, navigate]);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const cambiarTab = (nuevoTab) => {
    if (enviando) return;
    setTab(nuevoTab);
    setErrores({});
    setErrorGeneral(null);
    setRetryAfter(null);
  };

  const validar = () => {
    const errs = {};
    if (!RX_EMAIL.test(form.email)) errs.email = t('auth.errores.email_invalido');
    if (!form.password || form.password.length < 6) errs.password = t('auth.errores.password_corta');
    if (tab === 'signup') {
      if (form.password !== form.password2) errs.password2 = t('auth.errores.passwords_no_coinciden');
      if (!form.nombre.trim()) errs.nombre = t('auth.errores.nombre_requerido');
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorGeneral(null);
    setRetryAfter(null);
    if (!validar()) return;

    setEnviando(true);
    try {
      if (tab === 'login') {
        await login(form.email.trim(), form.password);
      } else {
        await signup({
          email: form.email.trim(),
          password: form.password,
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim() || undefined,
          telefono: form.telefono.trim() || undefined,
          idioma_preferido: (i18n.resolvedLanguage || 'es').toLowerCase(),
        });
      }
      // useEffect arriba redirige tras setUser (respeta ?next=).
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setRetryAfter(err.message);
          setErrorGeneral(t('auth.errores.RATE_LIMIT', { defaultValue: err.message }));
        } else if (err.code) {
          const traduccion = t('auth.errores.' + err.code, { defaultValue: '' });
          setErrorGeneral(traduccion || err.message || t('auth.errores.generico'));
        } else {
          setErrorGeneral(err.message || t('auth.errores.generico'));
        }
      } else {
        setErrorGeneral(t('auth.errores.generico'));
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <Link to="/" className="login-logo"><Logo /></Link>

        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'login'}
            className={'auth-tab' + (tab === 'login' ? ' active' : '')}
            onClick={() => cambiarTab('login')}
          >
            {t('auth.tab_login')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'signup'}
            className={'auth-tab' + (tab === 'signup' ? ' active' : '')}
            onClick={() => cambiarTab('signup')}
          >
            {t('auth.tab_signup')}
          </button>
        </div>

        <h1 className="login-titulo">
          {tab === 'login' ? t('auth.login.titulo') : t('auth.signup.titulo')}
        </h1>
        <p className="login-sub">
          {tab === 'login' ? t('auth.login.sub') : t('auth.signup.sub')}
        </p>

        {params.get('msg') === 'password_reset_ok' && (
          <div className="login-msg-banner">
            <CheckCircle2 size={16} strokeWidth={1.75} />
            <span>{t('auth.login.password_reset_ok')}</span>
          </div>
        )}

        <form className="login-form" onSubmit={submit} noValidate>
          <div className="modal-campo">
            <label htmlFor="login-email">{t('auth.email')}</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={form.email}
              onChange={setCampo('email')}
              placeholder={t('auth.email_placeholder')}
              disabled={enviando}
            />
            {errores.email && <div className="modal-error-campo">{errores.email}</div>}
          </div>

          <div className="modal-campo">
            <label htmlFor="login-pass">{t('auth.password')}</label>
            <input
              id="login-pass"
              type="password"
              autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
              value={form.password}
              onChange={setCampo('password')}
              placeholder={t('auth.password_placeholder')}
              disabled={enviando}
            />
            {errores.password && <div className="modal-error-campo">{errores.password}</div>}
          </div>

          {tab === 'signup' && (
            <>
              <div className="modal-campo">
                <label htmlFor="login-pass2">{t('auth.password2')}</label>
                <input
                  id="login-pass2"
                  type="password"
                  autoComplete="new-password"
                  value={form.password2}
                  onChange={setCampo('password2')}
                  placeholder={t('auth.password2_placeholder')}
                  disabled={enviando}
                />
                {errores.password2 && <div className="modal-error-campo">{errores.password2}</div>}
              </div>

              <div className="modal-campo">
                <label htmlFor="login-nombre">{t('auth.nombre')}</label>
                <input
                  id="login-nombre"
                  type="text"
                  autoComplete="given-name"
                  value={form.nombre}
                  onChange={setCampo('nombre')}
                  placeholder={t('auth.nombre_placeholder')}
                  disabled={enviando}
                />
                {errores.nombre && <div className="modal-error-campo">{errores.nombre}</div>}
              </div>

              <div className="auth-fila">
                <div className="modal-campo">
                  <label htmlFor="login-apel">{t('auth.apellidos')}</label>
                  <input
                    id="login-apel"
                    type="text"
                    autoComplete="family-name"
                    value={form.apellidos}
                    onChange={setCampo('apellidos')}
                    placeholder={t('auth.apellidos_placeholder')}
                    disabled={enviando}
                  />
                </div>
                <div className="modal-campo">
                  <label htmlFor="login-tel">{t('auth.telefono')}</label>
                  <input
                    id="login-tel"
                    type="tel"
                    autoComplete="tel"
                    value={form.telefono}
                    onChange={setCampo('telefono')}
                    placeholder={t('auth.telefono_placeholder')}
                    disabled={enviando}
                  />
                </div>
              </div>
            </>
          )}

          {errorGeneral && (
            <div className="modal-error-general">
              <AlertCircle size={16} strokeWidth={1.75} />
              <span>{errorGeneral}</span>
            </div>
          )}

          <button type="submit" className="btn-primary login-btn" disabled={enviando || !!retryAfter}>
            {enviando ? (
              <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('auth.enviando')}</>
            ) : (
              tab === 'login' ? t('auth.login.cta') : t('auth.signup.cta')
            )}
          </button>

          {tab === 'login' && (
            <p className="login-forgot">
              <Link to="/admin/forgot-password">{t('auth.login.forgot_password')}</Link>
            </p>
          )}

          <p className="auth-switch">
            {tab === 'login' ? (
              <>
                {t('auth.login.no_cuenta')}{' '}
                <button type="button" className="auth-link" onClick={() => cambiarTab('signup')} disabled={enviando}>
                  {t('auth.login.crea_cuenta')}
                </button>
              </>
            ) : (
              <>
                {t('auth.signup.tengo_cuenta')}{' '}
                <button type="button" className="auth-link" onClick={() => cambiarTab('login')} disabled={enviando}>
                  {t('auth.signup.entra')}
                </button>
              </>
            )}
          </p>
        </form>

        <p className="login-volver">
          <Link to="/">{t('admin.volver_inicio')}</Link>
        </p>
      </div>
    </div>
  );
}
